import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import {
  BookOpen, Link2, Copy, Check, ShieldCheck, Boxes, Wrench, MessageSquare, Lock,
  Eye, PencilLine, Globe, CheckCircle2, Clock,
} from 'lucide-react';

const URL_TEMPLATE = `${(typeof window !== 'undefined' ? window.location.origin : 'https://teamnova.ai')}/api/mcp/mcp?key=YOUR_CONNECTION_TOKEN`;

const CAP = {
  read: { label: 'Read', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', Icon: Eye },
  write: { label: 'Write', cls: 'bg-amber-50 text-amber-700 border-amber-200', Icon: PencilLine },
  global: { label: 'Global', cls: 'bg-purple-50 text-purple-700 border-purple-200', Icon: Globe },
};

const CapTag = ({ cap }) => {
  const c = CAP[cap] || CAP.read;
  const { Icon } = c;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium ${c.cls}`}>
      <Icon className="w-3 h-3" /> {c.label}
    </span>
  );
};

const TOOL_GROUPS = [
  {
    category: 'Compliance',
    tools: [
      { name: 'Compliance Status', cap: 'read', desc: 'Overall and per-framework posture: passing, failing, at-risk and coverage %.' },
      { name: 'Framework Status', cap: 'read', desc: 'Status and top gaps for one framework (e.g. SOC 2, ISO 27001).' },
      { name: 'Top Compliance Gaps', cap: 'read', desc: 'The most important failing / at-risk controls across the workspace.' },
      { name: 'Control Details', cap: 'read', desc: 'Full detail for one control: description, frameworks, status, required evidence and linked policy.' },
    ],
  },
  {
    category: 'Evidence',
    tools: [
      { name: 'Search Evidence', cap: 'read', desc: 'Find controls/evidence matching a keyword (e.g. "access control").' },
      { name: 'Missing Evidence', cap: 'read', desc: 'Controls that still require evidence but have none evaluated.' },
      { name: 'Stale Evidence', cap: 'read', desc: 'Evidence older than the freshness threshold, oldest first.' },
      { name: 'Evidence Summary', cap: 'read', desc: 'Evidence counts and latest collection time per connected source.' },
    ],
  },
  {
    category: 'Audits',
    tools: [
      { name: 'List Audits', cap: 'read', desc: 'All active audits with control counts and coverage.' },
      { name: 'Full Audit Details', cap: 'read', desc: 'Everything about one audit from just its name or id — status, phase, dates, readiness, control totals, top gaps, evidence & policy status, blockers. No workspace id needed.' },
      { name: 'Audit Status', cap: 'read', desc: 'Control counts by state and coverage for one audit.' },
      { name: 'Audit Readiness', cap: 'read', desc: 'Whether an audit is ready / nearly ready / not ready, with top gaps.' },
    ],
  },
  {
    category: 'Policies',
    tools: [
      { name: 'List Policies', cap: 'read', desc: 'Workspace policies with status, version and mapped frameworks.' },
      { name: 'Policy Status', cap: 'read', desc: 'Rollup of policies by status (draft / published / archived).' },
      { name: 'Policy Details', cap: 'read', desc: 'Metadata for one policy (the full body is never returned).' },
    ],
  },
  {
    category: 'Integrations',
    tools: [
      { name: 'List Integrations', cap: 'read', desc: 'Connected integrations and their status.' },
      { name: 'Integration Status', cap: 'read', desc: 'Connection + last-sync detail for one integration.' },
      { name: 'Trigger Integration Sync', cap: 'write', desc: 'Start a fresh sync for a connected integration.' },
      { name: 'Run Evidence Collection', cap: 'write', desc: 'Run evidence collection for a connected integration, then auto-evaluate controls.' },
    ],
  },
  {
    category: 'Global',
    tools: [
      { name: 'List Workspaces', cap: 'global', desc: 'Every Nova workspace (id + name). Global credentials only.' },
      { name: 'Workspace Compliance', cap: 'global', desc: 'Full compliance posture for a specific workspace. Global credentials only.' },
      { name: 'Global Compliance Summary', cap: 'global', desc: 'Coverage across all workspaces, lowest coverage first. Global credentials only.' },
    ],
  },
];

const INTEGRATIONS_AVAILABLE = [
  { name: 'GitHub', desc: 'Repository security posture, branch protection, and evidence for engineering controls.' },
  { name: 'Google Workspace', desc: 'Identity, admin, group and device-policy signals for access-control evidence.' },
  { name: 'Compliance', desc: 'Frameworks, controls, coverage and gap analysis across the workspace.' },
  { name: 'Audits', desc: 'Audit scope, control state and readiness.' },
  { name: 'Evidence', desc: 'Collected evidence, freshness, and what is still missing.' },
  { name: 'Policies', desc: 'Policy inventory, status and framework mapping.' },
];

const INTEGRATIONS_SOON = [
  { name: 'Microsoft 365', desc: 'Identity and admin signals from Entra ID / M365.' },
  { name: 'Device Trust', desc: 'Endpoint posture from enrolled devices.' },
  { name: 'Trust Center', desc: 'Public trust-center content and shared documents.' },
];

const PROMPT_GROUPS = [
  { category: 'Compliance', prompts: ['What is our compliance posture?', 'What are our biggest SOC 2 gaps?', 'Which controls are failing?', 'Show details for control AC-3.'] },
  { category: 'Evidence', prompts: ['Which evidence is stale?', 'Which evidence is missing?', 'Show evidence for Access Control.', 'Summarise our evidence sources.'] },
  { category: 'Audits', prompts: ['List all audits.', 'Tell me everything about audit SOC 2 2026.', 'Summarize this audit <audit-id>.', 'Is our SOC 2 audit ready?', 'Show audit progress.'] },
  { category: 'Integrations', prompts: ['Is GitHub connected?', 'Sync Google Workspace.', 'Run evidence collection.', 'When did GitHub last sync?'] },
  { category: 'Global', prompts: ['List all workspaces.', 'Which workspace has the lowest compliance score?', 'Give me a global compliance summary.'] },
];

const SECURITY_POINTS = [
  ['Every request is authenticated', 'Each call must carry the connection’s secure credential — anonymous access is never allowed.'],
  ['Every request is logged', 'All tool calls are recorded (success, blocked, or error) with a request ID in the Request Logs tab.'],
  ['Tokens can be rotated', 'Issue a fresh credential at any time from the connection’s actions; the old one stops working immediately.'],
  ['Tokens can be revoked', 'Disable a connection instantly — the credential is rejected on the next request.'],
  ['Tenant isolation is always enforced', 'A connection can only read the workspace(s) it was scoped to. Cross-workspace access is impossible.'],
  ['Least privilege by design', 'MCP only accesses the resources, capabilities and integrations the connection was granted — nothing more.'],
];

const Section = ({ id, icon: Icon, title, children }) => (
  <Card className="border-black/10" data-testid={`mcp-doc-${id}`}>
    <CardContent className="p-5">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-[#0F62FE]" />
        <h2 className="text-[15px] font-semibold text-[#0A0A0A]">{title}</h2>
      </div>
      {children}
    </CardContent>
  </Card>
);

export default function McpDocumentation() {
  const [copied, setCopied] = useState(null);
  const clip = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const t = document.createElement('textarea');
      t.value = text; t.style.position = 'fixed'; t.style.opacity = '0';
      document.body.appendChild(t); t.select();
      try { document.execCommand('copy'); } catch { /* noop */ }
      document.body.removeChild(t);
    }
    setCopied(key); setTimeout(() => setCopied(null), 1400);
  };

  return (
    <div className="space-y-4" data-testid="mcp-documentation">
      {/* Overview */}
      <Section id="overview" icon={BookOpen} title="Overview">
        <div className="text-[13px] text-black/70 space-y-2 leading-relaxed">
          <p><strong>Nova MCP</strong> is a secure Model Context Protocol server that lets AI assistants query your Nova compliance workspace in natural language — no exports, spreadsheets or copy-pasting required.</p>
          <p>Once connected, assistants such as <strong>ChatGPT</strong>, <strong>Claude Desktop</strong> and <strong>Cursor</strong> can ask about your compliance posture, audit readiness, control status, evidence freshness and integrations, and can trigger evidence collection — all with plain-English prompts.</p>
          <p>MCP connects to a specific Nova workspace through a unique, credentialed URL. Access is authenticated on every request, scoped to only the workspace(s) and permissions you grant, and fully logged.</p>
        </div>
      </Section>

      {/* Connection URL */}
      <Section id="connection-url" icon={Link2} title="Connection URL">
        <div className="text-[13px] text-black/70 space-y-2 leading-relaxed">
          <p>The <strong>MCP Server URL</strong> is what you paste into your AI client to connect it to Nova. Each connection you create generates a <strong>unique URL that contains its own secure credential</strong>, so it maps to exactly the workspace(s) and permissions of that connection.</p>
          <p className="text-black/60">Because the URL embeds a credential, treat it like a password — <strong>only share it with trusted AI clients</strong>. If it leaks, rotate or revoke the connection immediately.</p>
        </div>
        <div className="mt-3">
          <div className="text-[10px] uppercase tracking-wide text-black/40 mb-1">Format</div>
          <div className="flex items-center gap-2 rounded-lg border border-[#0F62FE]/30 bg-[#0F62FE]/5 p-2">
            <code className="font-mono text-[11px] break-all flex-1" data-testid="mcp-doc-url">{URL_TEMPLATE}</code>
            <button
              onClick={() => clip(URL_TEMPLATE, 'url')}
              data-testid="mcp-doc-copy-url"
              className="text-[#0F62FE] hover:text-[#0A4FCC] shrink-0"
              title="Copy URL format"
            >
              {copied === 'url' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            </button>
          </div>
          <p className="text-[11px] text-black/45 mt-1">Your real URL (with the live token) is shown once when you create a connection, on the <strong>MCP Connections</strong> tab.</p>
        </div>
      </Section>

      {/* Permissions */}
      <Section id="permissions" icon={ShieldCheck} title="Permissions">
        <p className="text-[13px] text-black/70 mb-3">Each connection is granted one or more permission levels. Tools require the matching level to run.</p>
        <div className="grid sm:grid-cols-3 gap-3">
          {[
            ['read', 'View compliance data — status, controls, evidence, audits and policies. Read-only, never changes anything.', 'e.g. “What is our compliance posture?”'],
            ['write', 'Take safe, pre-approved actions in Nova, such as starting an integration sync or evidence collection.', 'e.g. “Run evidence collection for GitHub.”'],
            ['global', 'Operate across all workspaces (Super Admin only) — list workspaces and compare compliance org-wide.', 'e.g. “Which workspace has the lowest score?”'],
          ].map(([cap, desc, ex]) => (
            <div key={cap} className="rounded-lg border border-black/10 p-3">
              <CapTag cap={cap} />
              <p className="text-[12px] text-black/70 mt-2 leading-relaxed">{desc}</p>
              <p className="text-[11px] text-black/45 mt-1.5 italic">{ex}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Available Integrations */}
      <Section id="integrations" icon={Boxes} title="Available Integrations">
        <p className="text-[13px] text-black/70 mb-3">What this MCP connection can reach, depending on the sources it was scoped to.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <div className="flex items-center gap-1.5 mb-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /><span className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">Available</span></div>
            <div className="space-y-2">
              {INTEGRATIONS_AVAILABLE.map((i) => (
                <div key={i.name} className="rounded-lg border border-black/10 p-2.5">
                  <div className="text-[13px] font-medium text-[#0A0A0A]">{i.name}</div>
                  <div className="text-[12px] text-black/55 leading-snug">{i.desc}</div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="flex items-center gap-1.5 mb-2"><Clock className="w-3.5 h-3.5 text-black/40" /><span className="text-[11px] font-semibold uppercase tracking-wide text-black/45">Coming Soon</span></div>
            <div className="space-y-2">
              {INTEGRATIONS_SOON.map((i) => (
                <div key={i.name} className="rounded-lg border border-dashed border-black/15 bg-black/[0.015] p-2.5">
                  <div className="text-[13px] font-medium text-black/60 flex items-center gap-2">{i.name}<Badge variant="outline" className="text-[9px] px-1.5 py-0 text-black/40 border-black/15">Soon</Badge></div>
                  <div className="text-[12px] text-black/45 leading-snug">{i.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* Available MCP Tools */}
      <Section id="tools" icon={Wrench} title="Available MCP Tools">
        <p className="text-[13px] text-black/70 mb-3">Grouped by category. Each tool shows what it does and the permission it needs.</p>
        <div className="grid md:grid-cols-2 gap-4">
          {TOOL_GROUPS.map((g) => (
            <div key={g.category}>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-[#0F62FE] mb-2">{g.category}</div>
              <div className="space-y-2">
                {g.tools.map((t) => (
                  <div key={t.name} className="rounded-lg border border-black/10 p-2.5" data-testid={`mcp-tool-${t.name.replace(/\s+/g, '-').toLowerCase()}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[13px] font-medium text-[#0A0A0A]">{t.name}</span>
                      <CapTag cap={t.cap} />
                    </div>
                    <div className="text-[12px] text-black/55 leading-snug mt-0.5">{t.desc}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Example Prompts */}
      <Section id="prompts" icon={MessageSquare} title="Example Prompts">
        <p className="text-[13px] text-black/70 mb-3">A library of prompts you can paste into your AI client. Click any to copy.</p>
        <div className="space-y-4">
          {PROMPT_GROUPS.map((g) => (
            <div key={g.category}>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-[#0F62FE] mb-2">{g.category}</div>
              <div className="grid sm:grid-cols-2 gap-2">
                {g.prompts.map((p, pi) => (
                  <button
                    key={p}
                    onClick={() => clip(p, p)}
                    data-testid={`mcp-doc-prompt-${g.category.toLowerCase()}-${pi}`}
                    title="Click to copy"
                    className="group flex items-center gap-2 text-left rounded-lg border border-black/10 bg-white px-3 py-2 text-[13px] text-black/75 hover:border-[#0F62FE]/40 hover:bg-[#0F62FE]/[0.04] transition-colors"
                  >
                    <MessageSquare className="w-3.5 h-3.5 text-[#0F62FE]/70 shrink-0" />
                    <span className="flex-1">{p}</span>
                    {copied === p ? <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0" /> : <Copy className="w-3.5 h-3.5 text-black/25 group-hover:text-[#0F62FE] shrink-0" />}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Security */}
      <Section id="security" icon={Lock} title="Security">
        <div className="grid sm:grid-cols-2 gap-3">
          {SECURITY_POINTS.map(([title, desc]) => (
            <div key={title} className="flex gap-2.5 rounded-lg border border-black/10 p-3">
              <ShieldCheck className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
              <div>
                <div className="text-[13px] font-medium text-[#0A0A0A]">{title}</div>
                <div className="text-[12px] text-black/55 leading-snug">{desc}</div>
              </div>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}
